import { create } from "zustand";
import { message } from "antd";
import {
  cancelDeliveryTask,
  createDeliveryTask,
  getGroupedDeliveries,
  updateDeliveryRecord,
} from "../services/Vendor";
import { debounce } from "lodash";

const useDeliveryStore = create((set, get) => ({
  // ── Server state ─────────────────────────────────────────────────────────
  records: [],
  total: 0,
  loading: false,
  error: null,

  // ── Filter + pagination state ─────────────────────────────────────────────
  searchText: "",
  statusFilter: "all",
  bookedByFilter: [],
  outletNameFilter: "",
  partnerFilter: [],

  // ── Filter actions ────────────────────────────────────────────────────────

  setSearchText: debounce((searchText) => {
    set({ searchText });
    get().fetchRecords();
  }, 500),

  setStatusFilter: (statusFilter) => {
    set({ statusFilter });
    get().fetchRecords();
  },

  setDrawerFilters: ({ bookedBy, outletName, partner }) => {
    set({
      bookedByFilter: bookedBy,
      outletNameFilter: outletName,
      partnerFilter: partner,
    });
    get().fetchRecords();
  },

  setLoading: (loading) => set({ loading }),

  fetchRecords: async () => {
    const {
      searchText,
      statusFilter,
      bookedByFilter,
      outletNameFilter,
      partnerFilter,
    } = get();

    set({ loading: true, error: null });

    try {
      const records = await getGroupedDeliveries({
        searchText,
        filters: {
          statusFilter,
          booked_by: bookedByFilter.length
            ? bookedByFilter.map((bookedBy) => bookedBy.toLowerCase())
            : undefined,
          outlet_name: outletNameFilter || undefined,
          delivery_partners: partnerFilter.length
            ? partnerFilter.map((partner) => partner.toLowerCase())
            : undefined,
        },
      });
      set({ records, total: records.length, loading: false });
    } catch (err) {
      set({ error: err.message, loading: false });
      message.error("Failed to load deliveries");
    }
  },

  createRecord: async ({
    id,
    partner,
    initiator = "airmenus_admin",
    delivery_attempts = [],
  }) => {
    set({ loading: true, error: null });

    try {
      const res = await createDeliveryTask(id, partner, initiator);

      if (partner === "multi") {
        const errors = res?.errors || [];
        errors.forEach((err) => message.error(err));
      }

      delivery_attempts
        .filter((attempt) => attempt.partner === "genie" && !attempt.is_deleted)
        .forEach(async (attempt) => {
          await cancelDeliveryTask({
            task_id: attempt.task_id,
            reason: "Cancelled by Admin",
            partner: "genie",
          });
        });

      get().fetchRecords();
    } catch (err) {
      set({ error: err.message, loading: false });
      message.error("Failed to create delivery");
    }
  },

  cancelRecord: async (task_id, reason, partner) => {
    set({ loading: true, error: null });
    try {
      await cancelDeliveryTask({
        task_id: task_id,
        reason: reason,
        partner: partner,
      });

      get().fetchRecords();
    } catch (err) {
      set({ error: err.message, loading: false });
      message.error("Failed to cancel delivery");
    }
  },

  markAttemptDelivered: async (attempt) => {
    set({ loading: true, error: null });
    try {
      await updateDeliveryRecord({
        ...attempt,
        status: "delivered",
      });

      get().fetchRecords();
    } catch (err) {
      set({ error: err.message, loading: false });
      message.error("Failed to update delivery");

      throw err.message;
    }
  },
}));

export default useDeliveryStore;
